/**
 * Quantity parsing and unit normalisation for German and English input.
 *
 * Everything the user types ("300g", "1,5 kg", "2 EL", "½ Becher") is reduced to
 * a `Quantity`: an amount plus one of four canonical units. Converting a
 * `Quantity` into an actual gram/millilitre weight needs food-specific context
 * (serving size, density), so that step lives in the API, not here.
 */
export const CANONICAL_UNITS = ['g', 'ml', 'piece', 'serving'];
/**
 * Alias table. Keys must be lowercase and free of punctuation — input is
 * normalised through `normalizeUnitKey` before lookup.
 *
 * Spoon measures are volumetric by convention (1 EL = 15 ml, 1 TL = 5 ml);
 * for a solid food the API converts ml to g using the food's density, which
 * defaults to 1.0.
 */
const UNIT_ALIASES = {
    // --- mass ---------------------------------------------------------------
    g: { unit: 'g', factor: 1 },
    gr: { unit: 'g', factor: 1 },
    gramm: { unit: 'g', factor: 1 },
    gram: { unit: 'g', factor: 1 },
    gramms: { unit: 'g', factor: 1 },
    grams: { unit: 'g', factor: 1 },
    kg: { unit: 'g', factor: 1000 },
    kilo: { unit: 'g', factor: 1000 },
    kilos: { unit: 'g', factor: 1000 },
    kilogramm: { unit: 'g', factor: 1000 },
    kilogram: { unit: 'g', factor: 1000 },
    mg: { unit: 'g', factor: 0.001 },
    milligramm: { unit: 'g', factor: 0.001 },
    milligram: { unit: 'g', factor: 0.001 },
    // --- volume -------------------------------------------------------------
    ml: { unit: 'ml', factor: 1 },
    milliliter: { unit: 'ml', factor: 1 },
    millilitre: { unit: 'ml', factor: 1 },
    cl: { unit: 'ml', factor: 10 },
    centiliter: { unit: 'ml', factor: 10 },
    dl: { unit: 'ml', factor: 100 },
    deziliter: { unit: 'ml', factor: 100 },
    l: { unit: 'ml', factor: 1000 },
    ltr: { unit: 'ml', factor: 1000 },
    liter: { unit: 'ml', factor: 1000 },
    litre: { unit: 'ml', factor: 1000 },
    liters: { unit: 'ml', factor: 1000 },
    // --- spoons and pinches -------------------------------------------------
    el: { unit: 'ml', factor: 15 },
    essloeffel: { unit: 'ml', factor: 15 },
    tbsp: { unit: 'ml', factor: 15 },
    tablespoon: { unit: 'ml', factor: 15 },
    tablespoons: { unit: 'ml', factor: 15 },
    tl: { unit: 'ml', factor: 5 },
    teeloeffel: { unit: 'ml', factor: 5 },
    tsp: { unit: 'ml', factor: 5 },
    teaspoon: { unit: 'ml', factor: 5 },
    teaspoons: { unit: 'ml', factor: 5 },
    prise: { unit: 'g', factor: 0.5 },
    pinch: { unit: 'g', factor: 0.5 },
    // --- countable ----------------------------------------------------------
    stk: { unit: 'piece', factor: 1 },
    st: { unit: 'piece', factor: 1 },
    stueck: { unit: 'piece', factor: 1 },
    piece: { unit: 'piece', factor: 1 },
    pieces: { unit: 'piece', factor: 1 },
    pcs: { unit: 'piece', factor: 1 },
    scheibe: { unit: 'piece', factor: 1 },
    scheiben: { unit: 'piece', factor: 1 },
    slice: { unit: 'piece', factor: 1 },
    slices: { unit: 'piece', factor: 1 },
    // Note: countable foods ("Ei", "Banane") are deliberately NOT units. A bare
    // number with no unit already means "pieces", so listing them here would
    // swallow the food name and leave the item unidentifiable.
    // --- portion-like -------------------------------------------------------
    portion: { unit: 'serving', factor: 1 },
    portionen: { unit: 'serving', factor: 1 },
    serving: { unit: 'serving', factor: 1 },
    servings: { unit: 'serving', factor: 1 },
    packung: { unit: 'serving', factor: 1 },
    packungen: { unit: 'serving', factor: 1 },
    pack: { unit: 'serving', factor: 1 },
    pck: { unit: 'serving', factor: 1 },
    dose: { unit: 'serving', factor: 1 },
    dosen: { unit: 'serving', factor: 1 },
    can: { unit: 'serving', factor: 1 },
    becher: { unit: 'serving', factor: 1 },
    cup: { unit: 'serving', factor: 1 },
    cups: { unit: 'serving', factor: 1 },
    tasse: { unit: 'serving', factor: 1 },
    glas: { unit: 'serving', factor: 1 },
    handvoll: { unit: 'serving', factor: 1 },
    handful: { unit: 'serving', factor: 1 },
    riegel: { unit: 'serving', factor: 1 },
    bar: { unit: 'serving', factor: 1 },
};
/** Every alias, longest first, so "kg" is preferred over "g" when scanning. */
export const UNIT_ALIAS_KEYS = Object.keys(UNIT_ALIASES).sort((a, b) => b.length - a.length);
/**
 * Folds German umlauts and strips punctuation so "Esslöffel", "esslöffel." and
 * "ESSLOEFFEL" all reach the same table entry.
 */
export function normalizeUnitKey(raw) {
    return raw
        .toLowerCase()
        .trim()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[.,;:]+$/g, '');
}
export function lookupUnit(raw) {
    return UNIT_ALIASES[normalizeUnitKey(raw)] ?? null;
}
export function isKnownUnit(raw) {
    return lookupUnit(raw) != null;
}
const VULGAR_FRACTIONS = {
    '½': 0.5,
    '⅓': 1 / 3,
    '⅔': 2 / 3,
    '¼': 0.25,
    '¾': 0.75,
    '⅕': 0.2,
    '⅙': 1 / 6,
    '⅛': 0.125,
};
/**
 * Parses a written amount into a number, accepting the German decimal comma,
 * ASCII fractions ("1/2") and unicode vulgar fractions ("½").
 * Returns `null` for anything it cannot read confidently.
 */
export function parseAmount(raw) {
    const text = raw.trim();
    if (text.length === 0)
        return null;
    const vulgar = VULGAR_FRACTIONS[text];
    if (vulgar != null)
        return vulgar;
    // "1/2" or "3 / 4"
    const fraction = /^(\d+)\s*\/\s*(\d+)$/.exec(text);
    if (fraction) {
        const numerator = Number(fraction[1]);
        const denominator = Number(fraction[2]);
        if (denominator === 0)
            return null;
        return numerator / denominator;
    }
    // "1,5" / "1.5" / "1500"
    const decimal = text.replace(',', '.');
    if (!/^\d*\.?\d+$/.test(decimal))
        return null;
    const value = Number(decimal);
    return Number.isFinite(value) ? value : null;
}
/**
 * Combines an amount and a unit token into a canonical `Quantity`.
 * Returns `null` when the unit is unknown, so callers can fall back to
 * treating the token as part of the food name.
 */
export function toQuantity(amount, unitToken) {
    const definition = lookupUnit(unitToken);
    if (!definition)
        return null;
    return { amount: amount * definition.factor, unit: definition.unit };
}
/** Human-readable rendering of a quantity, e.g. "300 g" or "1 Portion". */
export function formatQuantity(quantity, locale = 'de') {
    const amount = Number.isInteger(quantity.amount)
        ? String(quantity.amount)
        : quantity.amount.toFixed(1);
    const formatted = locale === 'de' ? amount.replace('.', ',') : amount;
    switch (quantity.unit) {
        case 'g':
            return `${formatted} g`;
        case 'ml':
            return `${formatted} ml`;
        case 'piece':
            return locale === 'de'
                ? `${formatted} Stück`
                : `${formatted} ${quantity.amount === 1 ? 'piece' : 'pieces'}`;
        case 'serving':
            return locale === 'de'
                ? `${formatted} Portion${quantity.amount === 1 ? '' : 'en'}`
                : `${formatted} ${quantity.amount === 1 ? 'serving' : 'servings'}`;
    }
}
//# sourceMappingURL=units.js.map